import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Globe,
} from "lucide-react";
import type { MaterialsFolder } from "@/lib/materials-api";
import { MaterialsFolderActionsMenu } from "@/components/materials/MaterialsFolderActionsMenu";
import {
  buildMaterialsFolderTree,
  canManageFolder,
  folderNodeKind,
  type MaterialsFolderTreeNode,
} from "@/lib/materials-folder-tree";
import { cn } from "@/lib/utils";

type FolderActionHandlers = {
  canWrite: boolean;
  folderIsEmpty: (folderId: number) => boolean;
  onRenameFolder: (folder: MaterialsFolder) => void;
  onDeleteFolder: (folder: MaterialsFolder) => void;
  onMoveFolder: (folder: MaterialsFolder) => void;
};

type Props = {
  folders: MaterialsFolder[];
  selectedFolderId: number | null;
  workRootId: number | null;
  rootLabel?: string;
  loading?: boolean;
  onNavigate: (folderId: number | null) => void;
} & Partial<FolderActionHandlers>;

/** 收集从根到目标文件夹（不含自身）的祖先 id，用于自动展开 */
function ancestorIds(
  folderId: number | null,
  byId: Map<number, MaterialsFolder>,
): number[] {
  if (folderId == null) return [];
  const chain: number[] = [];
  let cur = byId.get(folderId)?.parent_id ?? null;
  let guard = 0;
  while (cur != null && guard < 32) {
    chain.push(cur);
    cur = byId.get(cur)?.parent_id ?? null;
    guard += 1;
  }
  return chain;
}

/**
 * 素材库左侧目录树：展开/折叠 + 层级引导线（常见文件树样式）。
 * 「全部素材」为根；点击行导航，点击箭头仅展开/折叠。
 */
export function MaterialsFolderTree({
  folders,
  selectedFolderId,
  workRootId,
  rootLabel = "全部素材",
  loading = false,
  onNavigate,
  canWrite = false,
  folderIsEmpty,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
}: Props) {
  const folderById = useMemo(
    () => new Map(folders.map((f) => [f.id, f])),
    [folders],
  );
  const roots = useMemo(
    () => buildMaterialsFolderTree(folders, workRootId),
    [folders, workRootId],
  );

  /** 根节点「全部素材」是否展开其子文件夹 */
  const [rootExpanded, setRootExpanded] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());

  // 选中变化时展开其祖先，保证当前目录在树中可见
  useEffect(() => {
    const ancestors = ancestorIds(selectedFolderId, folderById);
    if (ancestors.length === 0 && selectedFolderId == null) {
      setRootExpanded(true);
      return;
    }
    setRootExpanded(true);
    if (ancestors.length === 0) return;
    setExpandedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ancestors) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedFolderId, folderById]);

  // 首次有数据时默认展开全部有子节点的文件夹，便于扫读层级
  useEffect(() => {
    if (folders.length === 0) return;
    setExpandedIds((prev) => {
      if (prev.size > 0) return prev;
      const withChildren = new Set<number>();
      const childCount = new Map<number, number>();
      for (const f of folders) {
        if (f.parent_id == null) continue;
        childCount.set(f.parent_id, (childCount.get(f.parent_id) ?? 0) + 1);
      }
      for (const [id, n] of childCount) {
        if (n > 0) withChildren.add(id);
      }
      return withChildren.size > 0 ? withChildren : prev;
    });
  }, [folders]);

  function toggleFolder(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const rootSelected = selectedFolderId == null;
  const hasRootChildren = roots.length > 0;

  return (
    <nav
      className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 text-sm"
      aria-label="素材目录"
    >
      <ul className="flex flex-col" role="tree">
        <li role="treeitem" aria-expanded={hasRootChildren ? rootExpanded : undefined}>
          <div
            className={cn(
              "group flex w-full items-stretch rounded-md hover:bg-muted",
              rootSelected && "bg-muted font-medium",
            )}
          >
            {hasRootChildren ? (
              <button
                type="button"
                className="flex w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label={rootExpanded ? `折叠${rootLabel}` : `展开${rootLabel}`}
                onClick={() => setRootExpanded((v) => !v)}
              >
                {rootExpanded ? (
                  <ChevronDown className="size-3.5" />
                ) : (
                  <ChevronRight className="size-3.5" />
                )}
              </button>
            ) : (
              <span className="w-5 shrink-0" aria-hidden />
            )}
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pr-2 text-left"
              onClick={() => onNavigate(null)}
            >
              {rootSelected ? (
                <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Folder className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{rootLabel}</span>
            </button>
          </div>

          {rootExpanded && hasRootChildren ? (
            <ul role="group" className="flex flex-col">
              {roots.map((node, index) => (
                <FolderTreeNode
                  key={node.folder.id}
                  node={node}
                  depth={0}
                  isLast={index === roots.length - 1}
                  ancestorContinues={[]}
                  selectedFolderId={selectedFolderId}
                  expandedIds={expandedIds}
                  onToggle={toggleFolder}
                  onNavigate={onNavigate}
                  canWrite={canWrite}
                  folderIsEmpty={folderIsEmpty}
                  onRenameFolder={onRenameFolder}
                  onDeleteFolder={onDeleteFolder}
                  onMoveFolder={onMoveFolder}
                />
              ))}
            </ul>
          ) : null}
        </li>
      </ul>

      {!loading && roots.length === 0 ? (
        <p className="px-2 py-3 text-xs text-muted-foreground">
          还没有文件夹。可「新建文件夹」，或直接在根目录新建文案/上传。
        </p>
      ) : null}
    </nav>
  );
}

type NodeProps = {
  node: MaterialsFolderTreeNode;
  depth: number;
  isLast: boolean;
  /** 每一层祖先是否仍有后续兄弟（决定竖线是否贯通） */
  ancestorContinues: boolean[];
  selectedFolderId: number | null;
  expandedIds: Set<number>;
  onToggle: (id: number) => void;
  onNavigate: (folderId: number | null) => void;
  canWrite: boolean;
  folderIsEmpty?: (folderId: number) => boolean;
  onRenameFolder?: (folder: MaterialsFolder) => void;
  onDeleteFolder?: (folder: MaterialsFolder) => void;
  onMoveFolder?: (folder: MaterialsFolder) => void;
};

function FolderTreeNode({
  node,
  depth,
  isLast,
  ancestorContinues,
  selectedFolderId,
  expandedIds,
  onToggle,
  onNavigate,
  canWrite,
  folderIsEmpty,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
}: NodeProps) {
  const { folder, children } = node;
  const hasChildren = children.length > 0;
  const expanded = hasChildren && expandedIds.has(folder.id);
  const selected = selectedFolderId === folder.id;

  const isEmpty = folderIsEmpty?.(folder.id) ?? false;
  const showActions =
    canWrite &&
    canManageFolder(folder) &&
    onRenameFolder &&
    onDeleteFolder &&
    onMoveFolder &&
    folderIsEmpty;
  const nodeKind = folderNodeKind(folder);
  const FolderIcon =
    nodeKind === "brand_root"
      ? Building2
      : nodeKind === "brand_site_root"
        ? Globe
        : selected
          ? FolderOpen
          : Folder;

  return (
    <li role="treeitem" aria-expanded={hasChildren ? expanded : undefined}>
      <div
        className={cn(
          "group flex w-full items-stretch rounded-md hover:bg-muted",
          selected && "bg-muted font-medium",
        )}
      >
        {/* 祖先层级：贯通竖线或留白 */}
        {ancestorContinues.map((continues, i) => (
          <span
            key={`g-${folder.id}-${i}`}
            className="relative w-3 shrink-0 self-stretch"
            aria-hidden
          >
            {continues ? (
              <span className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-border" />
            ) : null}
          </span>
        ))}

        {/* 当前层：竖线拐角 + 横线 */}
        <span className="relative w-3 shrink-0 self-stretch" aria-hidden>
          <span className="absolute top-0 left-1/2 h-1/2 w-px -translate-x-1/2 bg-border" />
          {!isLast ? (
            <span className="absolute top-1/2 bottom-0 left-1/2 w-px -translate-x-1/2 bg-border" />
          ) : null}
          <span className="absolute top-1/2 right-0 left-1/2 h-px bg-border" />
        </span>

        {hasChildren ? (
          <button
            type="button"
            className="flex w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label={expanded ? `折叠 ${folder.name}` : `展开 ${folder.name}`}
            onClick={() => onToggle(folder.id)}
          >
            {expanded ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
          </button>
        ) : (
          <span className="w-5 shrink-0" aria-hidden />
        )}

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pr-1 text-left"
          onClick={() => onNavigate(folder.id)}
        >
          <FolderIcon
            className={cn(
              "size-3.5 shrink-0",
              nodeKind === "normal" ? "text-muted-foreground" : "text-primary",
            )}
          />
          <span className={cn("truncate", nodeKind !== "normal" && "font-medium")}>
            {folder.name}
          </span>
        </button>

        {showActions ? (
          <div className="flex items-center pr-0.5">
            <MaterialsFolderActionsMenu
              canWrite={canWrite}
              isEmpty={isEmpty}
              compact
              onRename={() => onRenameFolder(folder)}
              onMove={() => onMoveFolder(folder)}
              onDelete={() => onDeleteFolder(folder)}
            />
          </div>
        ) : null}
      </div>

      {expanded ? (
        <ul role="group" className="flex flex-col">
          {children.map((child, index) => (
            <FolderTreeNode
              key={child.folder.id}
              node={child}
              depth={depth + 1}
              isLast={index === children.length - 1}
              ancestorContinues={[...ancestorContinues, !isLast]}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onNavigate={onNavigate}
              canWrite={canWrite}
              folderIsEmpty={folderIsEmpty}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveFolder={onMoveFolder}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
